import { Auth0Client, AuthenticationError } from '@auth0/auth0-spa-js';
import { delay } from '@utils/common';
import dayjs from 'dayjs';
import { CONFIG } from '../config';
import { broadcastLogout, navigateToLogoutLanding } from './crossTabLogout';

const auth0 = new Auth0Client({
  domain: CONFIG.auth.domain ?? '',
  clientId: CONFIG.auth.clientId ?? '',
  authorizationParams: {
    scope: 'client_credentials',
    audience: 'onevest',
  },
  useRefreshTokens: true,
  cacheLocation: 'localstorage',
  useCookiesForTransactions: true,
  useRefreshTokensFallback: true,
});

export const JWT_CLAIMS_NAMESPACE = 'https://onevest.com';

export const getOrganizationKeyFromURL = (organizationName?: string) => {
  let organizationKey: string | null = null;
  let auth0RedirectUri = CONFIG.app.basePath || '';
  if (
    typeof window !== 'undefined' &&
    ['staging', 'production', 'casandbox', 'ussandbox'].includes(
      CONFIG.app.env ?? '',
    )
  ) {
    const { host, origin } = window.location;
    const key = host.split('.')?.[0];
    if (key && !['dev-onevest-app', 'onevest-app'].includes(key)) {
      organizationKey = key;
      auth0RedirectUri = origin;
    } else if (key && organizationName && CONFIG.app.orgBasePath) {
      organizationKey = organizationName;
      auth0RedirectUri = CONFIG.app.orgBasePath.replace(
        'organization',
        organizationName,
      );
    }

    console.log({
      event: 'ORGANIZATION_AUTH',
      organizationKey,
      auth0RedirectUri,
      url: origin,
    });
  } else if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1')
  ) {
    const localOrigin = new URL(CONFIG.app.basePath || window.location.origin);
    localOrigin.hostname = 'localhost';
    auth0RedirectUri = localOrigin.origin;
  }
  return { organizationKey, auth0RedirectUri };
};

/** Hard-navigate listener tabs to the org login landing after cross-tab logout. */
export const navigateToCrossTabLogoutLanding = (): void => {
  const { auth0RedirectUri } = getOrganizationKeyFromURL();
  navigateToLogoutLanding(auth0RedirectUri || window.location.origin);
};

/** Returns true when Auth0 logout ran and cross-tab broadcast was sent via openUrl. */
export const auth0Logout = async ({
  redirectUrl,
  federated,
}: {
  redirectUrl?: string;
  federated?: boolean;
} = {}): Promise<boolean> => {
  const isAuthenticatedWithAuth0 = await auth0.isAuthenticated();
  if (isAuthenticatedWithAuth0) {
    const { auth0RedirectUri } = getOrganizationKeyFromURL();
    await auth0.logout({
      logoutParams: {
        returnTo: redirectUrl ?? auth0RedirectUri,
        ...(federated ? { federated: true } : {}),
      },
      openUrl(url: string) {
        broadcastLogout();
        window.location.assign(url);
      },
    });
    await delay(500); // Add 500ms delay to handle external redirect as [setResetKey in ovApolloProvider] resets app state and causes a race condition
    return true;
  }

  return false;
};

/** Navigates to Auth0 /v2/logout; Auth0 validates returnTo against Allowed Logout URLs. */
export const redirectToAuth0Logout = (returnTo: string) => {
  const domain = CONFIG.auth.domain ?? '';
  const clientId = CONFIG.auth.clientId ?? '';
  const params = new URLSearchParams({
    client_id: clientId,
    returnTo,
  });
  window.location.assign(`https://${domain}/v2/logout?${params.toString()}`);
};

const toBase64Url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

export const redirectToAuth0Invitation = async ({
  invitation,
  organization,
  redirectUri,
}: {
  invitation: string;
  organization: string;
  redirectUri: string;
}) => {
  const { origin } = window.location;
  if (new URL(redirectUri, origin).origin === origin) {
    await auth0.loginWithRedirect({
      appState: { invitation: true },
      authorizationParams: {
        invitation,
        organization,
        redirect_uri: redirectUri,
      },
    });
    return;
  }

  // Another origin can't read this origin's spa-js transaction cookie, so it gets a
  // code nobody can redeem (verifier discarded) and signs in through its own login.
  // Fragment mode keeps the code out of its server logs and returns Auth0 errors
  // where the link-expired check reads them.
  const codeVerifier = toBase64Url(crypto.getRandomValues(new Uint8Array(32)));
  const codeChallenge = toBase64Url(
    new Uint8Array(
      await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(codeVerifier),
      ),
    ),
  );
  const params = new URLSearchParams({
    client_id: CONFIG.auth.clientId ?? '',
    invitation,
    organization,
    redirect_uri: redirectUri,
    response_type: 'code',
    response_mode: 'fragment',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });
  window.location.assign(
    `https://${CONFIG.auth.domain ?? ''}/authorize?${params.toString()}`,
  );
};

export const isAuth0InvitationError = (error: unknown) =>
  error instanceof AuthenticationError && error.appState?.invitation === true;

export const removeStatelessCodeFromHash = () => {
  const url = new URL(window.location.href);
  const hashParams = new URLSearchParams(url.hash.substring(1));
  if (!hashParams.get('code') || hashParams.has('state')) return;

  hashParams.delete('code');
  url.hash = hashParams.toString();
  window.history.replaceState(null, '', url.toString());
};

export const auth0RefreshToken = async ({
  redirectToPath,
  orgSubdomain,
}: {
  redirectToPath?: string;
  orgSubdomain?: string;
} = {}) => {
  const locationPath = redirectToPath ?? window.location.pathname;
  const shouldRedirectToPath = locationPath?.trim() !== '/';
  if (shouldRedirectToPath) {
    const auth0RedirectPath = {
      locationPath,
      expireAt: dayjs().add(5, 'minutes').toDate(),
    };
    localStorage.setItem(
      'auth0RedirectPath',
      JSON.stringify(auth0RedirectPath),
    );
  }
  const { auth0RedirectUri, organizationKey } = getOrganizationKeyFromURL();

  const orgKey = orgSubdomain ?? organizationKey;

  await auth0.loginWithRedirect({
    authorizationParams: {
      redirect_uri: auth0RedirectUri,
      ...(orgKey ? { organization: orgKey } : {}),
    },
  });
};

export const requireAuth0AccessToken = (token: string | undefined) => {
  if (!token) {
    throw new Error('Missing access token');
  }
  return token;
};

export const auth0RefreshTokenSilently = async ({
  redirectToPath,
  orgSubdomain,
}: {
  redirectToPath?: string;
  orgSubdomain?: string;
} = {}) => {
  const { organizationKey } = getOrganizationKeyFromURL();

  const orgKey = orgSubdomain ?? organizationKey;

  try {
    const newToken = await auth0.getTokenSilently({
      cacheMode: 'off',
      authorizationParams: {
        ...(orgKey ? { organization: orgKey } : {}),
      },
    });
    return requireAuth0AccessToken(newToken);
  } catch (e: any) {
    auth0RefreshToken({ redirectToPath, orgSubdomain });
    throw new Error(e);
  }
};

export default auth0;
